import React, { ReactNode, SetStateAction, createContext, useCallback, useContext, useMemo, useState } from 'react';

import { OfferOverrides } from 'modules/shared-menu/types';
import { getTypedKeys } from 'modules/shared-menu/utils';

import { useSelectionInfoContext } from '../../../contexts/SelectionInfoContext';
import { OverridableRowGroup, rowGroupToOfferAttrsMap, rowGroupToVenueOverrideSettingKeyMap } from '../constants';
import { useOverrideSettings } from '../hooks/useSelectionOverrideSettings';
import { useVenueSelectionOverrides } from '../hooks/useSelectionOverrides';
import { Offer } from '../types';
import { applyOverridesToOffer, getOfferOverrides } from '../utils';

type OfferOverridesContextData = {
  currOverriddenRowGroups: OverridableRowGroup[];
  setCurrOverriddenRowGroups: React.Dispatch<SetStateAction<OverridableRowGroup[]>>;
  offerOverrides: OfferOverrides;
  maybeOverriddenOffer: Offer | undefined;
  getOfferAttrHasOverride: (attr: keyof Offer) => boolean;
  currOverriddenOfferAttrs: (keyof Offer)[];
  getRowGroupCanBeOverridden: (rowGroup: OverridableRowGroup) => boolean;
  getRowGroupHasOverride: (rowGroup: OverridableRowGroup) => boolean;
};

const OfferOverridesContext = createContext<OfferOverridesContextData>({} as OfferOverridesContextData);

export const OfferOverridesProvider = ({ children, offer }: { children: ReactNode; offer?: Offer }) => {
  const { selectionOverrides } = useVenueSelectionOverrides();
  const { overrideSettings } = useOverrideSettings();
  const { vlmeConfig } = useSelectionInfoContext();
  const isVlme = !!vlmeConfig;
  const isSharedVenue = isVlme && vlmeConfig.isSharedVenue;

  const offerOverrides = useMemo(() => getOfferOverrides(offer, selectionOverrides), [offer, selectionOverrides]);

  const [currOverriddenRowGroups, setCurrOverriddenRowGroups] = useState(() => {
    const overriddenRowGroups = getTypedKeys(rowGroupToOfferAttrsMap).reduce((acc, rowGroup) => {
      const rowGroupHasOverride = rowGroupToOfferAttrsMap[rowGroup].some(attr => attr in offerOverrides);

      return [...acc, ...(rowGroupHasOverride ? [rowGroup] : [])];
    }, []);

    return overriddenRowGroups;
  });

  const getOfferAttrHasOverride = useCallback((attr: keyof Offer) => attr in offerOverrides, [offerOverrides]);

  const getRowGroupCanBeOverridden = useCallback(
    (rowGroup: OverridableRowGroup) => {
      if (!isSharedVenue) {
        return false;
      }

      const venueSettingKey = rowGroupToVenueOverrideSettingKeyMap[rowGroup];
      return !!overrideSettings && overrideSettings[venueSettingKey] === true;
    },
    [isSharedVenue, overrideSettings],
  );

  const getRowGroupHasOverride = useCallback(
    (rowGroup: OverridableRowGroup) => {
      if (!isSharedVenue) {
        return false;
      }

      const offerAttrsOfRowGroup = rowGroupToOfferAttrsMap[rowGroup];
      return offerAttrsOfRowGroup.some(attr => getOfferAttrHasOverride(attr));
    },
    [getOfferAttrHasOverride, isSharedVenue],
  );

  const maybeOverriddenOffer = offer ? applyOverridesToOffer(offer, offerOverrides) : undefined;

  const currOverriddenOfferAttrs = useMemo<(keyof Offer)[]>(
    () => Object.values(currOverriddenRowGroups).flatMap(rowGroup => rowGroupToOfferAttrsMap[rowGroup]),
    [currOverriddenRowGroups],
  );

  return (
    <OfferOverridesContext.Provider
      value={{
        offerOverrides,
        maybeOverriddenOffer,
        getRowGroupCanBeOverridden,
        getRowGroupHasOverride,
        setCurrOverriddenRowGroups,
        currOverriddenRowGroups,
        currOverriddenOfferAttrs,
        getOfferAttrHasOverride,
      }}
    >
      {children}
    </OfferOverridesContext.Provider>
  );
};

export const useOfferOverridesContext = () => useContext(OfferOverridesContext);
